import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';

const AttendanceFilters = ({
  selectedClass,
  classes,
  onClassFilterChange,
  selectedSection,
  sections,
  onSectionFilterChange,
  selectedDate,
  onDateChange,
  handleSetHoliday,
  handleShowHolidays,
  user,
  viewMode,
  holidayFilterType,
  setHolidayFilterType,
  holidaySearchQuery,
  setHolidaySearchQuery
}) => {
  const holidayFilterOptions = [
    { label: 'By Month', value: 'month' },
    { label: 'By Year', value: 'year' }
  ];

  return (
    <div className="mb-2 flex-shrink-0">
      <div className="bg-white/80 backdrop-blur-xl rounded-xl shadow-lg border border-white/20 p-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-indigo-500/10 rounded-full -translate-y-16 translate-x-16"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-400/10 to-pink-400/10 rounded-full translate-y-12 -translate-x-12"></div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg">
              <i className={`pi ${viewMode === 'attendance' ? 'pi-book' : 'pi-calendar'} text-white text-sm`}></i>
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">
                {viewMode === 'attendance' ? 'Attendance Management' : 'Holidays Directory'}
              </h3>
              <p className="text-xs text-gray-600 mt-0.5">
                {viewMode === 'attendance' ? 'Track and mark student attendance' : 'View and manage school holidays'}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <button 
              onClick={handleShowHolidays} 
              className={`bg-gradient-to-r ${viewMode === 'attendance' ? 'from-blue-500 to-indigo-600' : 'from-gray-500 to-gray-600'} text-white px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2 font-semibold shadow-lg hover:shadow-xl text-sm`}
            >
              <i className={`pi ${viewMode === 'attendance' ? 'pi-calendar-plus' : 'pi-users'} text-sm`}></i> 
              {viewMode === 'attendance' ? 'Show Holidays' : 'Show Attendance'}
            </button>
            {(user?.role === 'admin' || user?.role === 'teacher') && (
              <button 
                onClick={handleSetHoliday} 
                className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 transition-all duration-300 flex items-center gap-2 font-semibold shadow-lg hover:shadow-xl text-sm"
              >
                <i className="pi pi-sun text-sm"></i> Set Holiday
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {viewMode === 'attendance' ? (
            <>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 flex items-center">
                  <i className="pi pi-book mr-1 text-indigo-500 text-xs"></i>Class
                </label>
                <Dropdown 
                  value={selectedClass} 
                  options={classes.map(c => ({ label: c.name || c.className, value: c.id }))} 
                  onChange={onClassFilterChange} 
                  placeholder="Select Class" 
                  className="w-full text-sm border border-gray-200 rounded-lg" 
                  panelClassName="border border-gray-200 rounded-lg shadow-lg" 
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 flex items-center">
                  <i className="pi pi-th-large mr-1 text-purple-500 text-xs"></i>Section
                </label>
                <Dropdown 
                  value={selectedSection} 
                  options={sections.map(s => ({ label: s.name || s.sectionName, value: s.id }))} 
                  onChange={onSectionFilterChange} 
                  placeholder="Select Section" 
                  disabled={!selectedClass} 
                  className="w-full text-sm border border-gray-200 rounded-lg" 
                  panelClassName="border border-gray-200 rounded-lg shadow-lg" 
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 flex items-center">
                  <i className="pi pi-calendar mr-1 text-blue-500 text-xs"></i>Date
                </label>
                <Calendar 
                  value={selectedDate} 
                  onChange={(e) => onDateChange(e.value)} 
                  dateFormat="yy-mm-dd" 
                  showIcon 
                  className="w-full text-sm" 
                  inputClassName="border border-gray-200 rounded-lg p-2"
                />
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 flex items-center">
                  <i className="pi pi-filter mr-1 text-indigo-500 text-xs"></i>Filter By
                </label>
                <SelectButton 
                  value={holidayFilterType} 
                  options={holidayFilterOptions} 
                  onChange={(e) => e.value && setHolidayFilterType(e.value)} 
                  className="w-full text-sm"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 flex items-center">
                  <i className="pi pi-calendar mr-1 text-blue-500 text-xs"></i>
                  {holidayFilterType === 'month' ? 'Select Month & Year' : 'Select Year'}
                </label>
                <Calendar 
                  value={selectedDate} 
                  onChange={(e) => onDateChange(e.value)} 
                  view={holidayFilterType === 'month' ? 'month' : 'year'}
                  dateFormat={holidayFilterType === 'month' ? 'mm/yy' : 'yy'}
                  showIcon 
                  className="w-full text-sm" 
                  inputClassName="border border-gray-200 rounded-lg p-2"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 flex items-center">
                  <i className="pi pi-search mr-1 text-purple-500 text-xs"></i>Search Holidays
                </label>
                <span className="p-input-icon-left w-full flex items-center relative">
                  {/* <i className="pi pi-search absolute left-3 text-gray-400" /> */}
                  <InputText 
                    value={holidaySearchQuery} 
                    onChange={(e) => setHolidaySearchQuery(e.target.value)} 
                    placeholder="Search by title..." 
                    className="w-full text-sm border border-gray-200 rounded-lg p-2 pl-10" 
                  />
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceFilters;
